<template>
  <div class="slide slide-who5-breakdown">
    <!-- Header -->
    <div class="slide-header">
      <h2 class="slide-title">{{ data.title }}</h2>
      <div v-if="data.subtitle" class="slide-subtitle">{{ data.subtitle }}</div>
    </div>

    <!-- Questions Grid -->
    <div class="questions-container">
      <div
        v-for="(question, index) in data.questions"
        :key="index"
        class="question-row"
        :style="{ animationDelay: `${index * 0.1}s` }"
      >
        <!-- Question Label -->
        <div class="question-label">{{ question.label }}</div>

        <!-- Stacked Bar -->
        <div class="bar-container">
          <div class="stacked-bar">
            <div
              v-for="(segment, segIndex) in getSegments(question.distribution)"
              :key="segIndex"
              class="bar-segment"
              :class="segment.colorClass"
              :style="{ width: segment.width + '%' }"
              :title="`${segment.label}: ${segment.count} responses`"
            ></div>
          </div>
          <div class="bar-labels">
            <span class="label-good">{{ $t('reports.quarterly.who5.label_good') }}</span>
            <span class="label-poor">{{ $t('reports.quarterly.who5.label_poor') }}</span>
          </div>
        </div>

        <!-- Score & Change -->
        <div class="score-container">
          <div class="score-value">{{ question.score }}</div>
          <div class="score-change" :class="getChangeClass(question.trend)">
            <svg v-if="question.trend === 'up'" class="trend-icon" width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path d="M12 5L5 12H9V19H15V12H19L12 5Z" fill="currentColor"/>
            </svg>
            <svg v-else-if="question.trend === 'down'" class="trend-icon" width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path d="M12 19L19 12H15V5H9V12H5L12 19Z" fill="currentColor"/>
            </svg>
            <span>{{ formatChange(question.change) }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Legend -->
    <div class="legend">
      <div class="legend-item">
        <div class="legend-color legend-5"></div>
        <span>{{ $t('reports.quarterly.who5.legend_5') }}</span>
      </div>
      <div class="legend-item">
        <div class="legend-color legend-4"></div>
        <span>{{ $t('reports.quarterly.who5.legend_4') }}</span>
      </div>
      <div class="legend-item">
        <div class="legend-color legend-3"></div>
        <span>{{ $t('reports.quarterly.who5.legend_3') }}</span>
      </div>
      <div class="legend-item">
        <div class="legend-color legend-2"></div>
        <span>{{ $t('reports.quarterly.who5.legend_2') }}</span>
      </div>
      <div class="legend-item">
        <div class="legend-color legend-1"></div>
        <span>{{ $t('reports.quarterly.who5.legend_1') }}</span>
      </div>
      <div class="legend-item">
        <div class="legend-color legend-0"></div>
        <span>{{ $t('reports.quarterly.who5.legend_0') }}</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'WHO5BreakdownSlide',
  props: {
    data: {
      type: Object,
      required: true
    },
    slideId: {
      type: String,
      default: ''
    }
  },
  methods: {
    getSegments(distribution) {
      if (!distribution) return [];

      // Calculate total responses
      const total = Object.values(distribution).reduce((sum, count) => sum + count, 0);
      if (total === 0) return [];

      // Stored WHO-5 answers are option indexes: 0 is "All of the time"
      // and 5 is "At no time". Render the slide in the same order as the
      // insights summary: good responses on the left, poor responses on the right.
      const segments = [
        { label: this.$t('reports.quarterly.who5.legend_5'), count: distribution[0] || 0, colorClass: 'segment-5' },
        { label: this.$t('reports.quarterly.who5.legend_4'), count: distribution[1] || 0, colorClass: 'segment-4' },
        { label: this.$t('reports.quarterly.who5.legend_3'), count: distribution[2] || 0, colorClass: 'segment-3' },
        { label: this.$t('reports.quarterly.who5.legend_2'), count: distribution[3] || 0, colorClass: 'segment-2' },
        { label: this.$t('reports.quarterly.who5.legend_1'), count: distribution[4] || 0, colorClass: 'segment-1' },
        { label: this.$t('reports.quarterly.who5.legend_0'), count: distribution[5] || 0, colorClass: 'segment-0' }
      ];

      // Calculate widths
      return segments.map(seg => ({
        ...seg,
        width: (seg.count / total) * 100
      }));
    },
    getChangeClass(trend) {
      if (trend === 'up') return 'change-positive';
      if (trend === 'down') return 'change-negative';
      return 'change-neutral';
    },
    formatChange(change) {
      if (change === 0 || change === undefined) return '—';
      const prefix = change > 0 ? '+' : '';
      return `${prefix}${change}`;
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../../../../assets/quarterly-slides.scss';

.slide-who5-breakdown {
  @extend .slide-base;
  background: $paper;
  padding: 80px;
  display: flex;
  flex-direction: column;
}

.slide-header {
  margin-bottom: 48px;
}

.slide-title {
  @extend .serif-headline;
  font-size: 56px;
  color: $ink;
  margin: 0;
}

.slide-subtitle {
  font-family: $font-body;
  font-size: 20px;
  color: $ink-muted;
  margin-top: 12px;
}

.questions-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 28px;
}

.question-row {
  display: grid;
  grid-template-columns: 1fr 2fr 120px;
  gap: 32px;
  align-items: center;
  animation: fadeInUp 0.5s ease backwards;
}

.question-label {
  font-family: $font-body;
  font-size: 18px;
  color: $ink;
  line-height: 1.4;
}

.bar-container {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.stacked-bar {
  display: flex;
  height: 32px;
  border-radius: 16px;
  overflow: hidden;
  background: $paper-cool;
}

.bar-segment {
  height: 100%;
  transition: width 0.6s ease;

  // Match the WHO-5 colors used in the insights report modal.
  &.segment-0 {
    background: #d33e3e;
  }

  &.segment-1 {
    background: #ff4e4e;
  }

  &.segment-2 {
    background: #eed24e;
  }

  &.segment-3 {
    background: #76ca7d;
  }

  &.segment-4 {
    background: #00b383;
  }

  &.segment-5 {
    background: #068462;
  }
}

.bar-labels {
  display: flex;
  justify-content: space-between;
  font-family: $font-body;
  font-size: 12px;
  color: $ink-subtle;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.score-container {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
}

.score-value {
  font-family: $font-body;
  font-size: 36px;
  font-weight: 700;
  color: $ink;
  line-height: 1;
}

.score-change {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-family: $font-body;
  font-size: 14px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 12px;

  &.change-positive {
    background: rgba($emerald, 0.1);
    color: $emerald;
  }

  &.change-negative {
    background: rgba($rose, 0.1);
    color: $rose;
  }

  &.change-neutral {
    background: rgba($ink, 0.05);
    color: $ink-muted;
  }

  .trend-icon {
    width: 14px;
    height: 14px;
  }
}

.legend {
  display: flex;
  justify-content: center;
  gap: 32px;
  margin-top: 40px;
  padding-top: 24px;
  border-top: 1px solid rgba($ink, 0.1);
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: $font-body;
  font-size: 14px;
  color: $ink-muted;
}

.legend-color {
  width: 16px;
  height: 16px;
  border-radius: 4px;

  &.legend-0 {
    background: #d33e3e;
  }

  &.legend-1 {
    background: #ff4e4e;
  }

  &.legend-2 {
    background: #eed24e;
  }

  &.legend-3 {
    background: #76ca7d;
  }

  &.legend-4 {
    background: #00b383;
  }

  &.legend-5 {
    background: #068462;
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
